import React, { useRef, useState, useEffect, useContext } from 'react';

import { Link as RouterLink, useMatch } from 'react-router-dom';

import PropTypes from 'prop-types';

import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Link,
  Stack,
  styled,
  Drawer,
} from '@mui/material';

import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

import { motion } from 'framer-motion';

import GreenLogo from '../Assets/Logo/green.png';
import WhiteLogo from '../Assets/Logo/white.png';

import { COLORS } from '../Constant/COLORS';

import Signup from './Modals/Signup/index';
import Login from './Modals/Login';

import {
  NavItemDashboard,
  NavItemHome,
  NavItemJoin,
  NavItemLoggedUser,
  NavItemLogout,
  NavItemSignIn,
  useIsLoggedUser,
} from './HomeNavigationItems';

import AuthContext from '../Context/AuthContext';

import { useIsMobile } from '../Hooks/useIsMobile';

const TypoNav = styled(Typography)({
  color: COLORS.inkSoft,
  fontWeight: 500,
});

// Tracks whether the page has been scrolled past a small threshold, used to
// switch the header into its condensed "glass" state.
const useScrolled = (threshold = 8) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);
  return scrolled;
};

const Header = ({ isRegisterProfessionalPage, isProfessionalPage }) => {
  const { user } = useContext(AuthContext);

  const [isSignInModalVisible, setIsSignInModalVisible] = useState(false);
  const [isJoinModalVisible, setIsJoinModalVisible] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isDrawerButtonAvailable, setIsDrawerButtonAvailable] = useState(true);

  const drawerRef = useRef();

  const isRegisterProfessionalHome = useMatch('professional');

  const isMobile = useIsMobile();
  const isLoggedUser = useIsLoggedUser();
  const scrolled = useScrolled();

  const makeSignInModelVisible = () => setIsSignInModalVisible(true);
  const makeSignInModelHidden = () => setIsSignInModalVisible(false);

  const makeJoinModelVisible = () => setIsJoinModalVisible(true);
  const makeJoinModelHidden = () => setIsJoinModalVisible(false);

  const handleClickOpenNavMenu = () => setIsDrawerOpen(true);
  const handleCloseDrawer = () => setIsDrawerOpen(false);

  const shouldMobileNavTogglerVisible = () => {
    if (drawerRef.current)
      setIsDrawerButtonAvailable(
        Boolean(drawerRef.current.querySelectorAll('.nav-item').length)
      );
  };

  useEffect(shouldMobileNavTogglerVisible, [user]);

  useEffect(() => {
    const resizeFunc = () => {
      window.matchMedia('max-width(900px)').matches &&
        shouldMobileNavTogglerVisible();
    };
    window.addEventListener('resize', resizeFunc);

    return () => {
      window.removeEventListener('resize', resizeFunc);
    };
  }, []);

  const isBrandArea = isRegisterProfessionalPage || isProfessionalPage;
  const logoSrc = isBrandArea ? WhiteLogo : GreenLogo;

  // Glass background: translucent white (or translucent teal on brand areas)
  // once scrolled, fully solid at the top of the page.
  const headerBg = isBrandArea
    ? scrolled
      ? 'rgba(1,64,79,0.82)'
      : COLORS.teal
    : scrolled
    ? 'rgba(255,255,255,0.78)'
    : COLORS.surface;

  return (
    <>
      <AppBar
        position='sticky'
        elevation={0}
        component={motion.header}
        initial={{ y: -70, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        sx={{
          top: 0,
          zIndex: theme => theme.zIndex.appBar + 2,
          backgroundColor: headerBg,
          backdropFilter: scrolled ? 'saturate(180%) blur(14px)' : 'none',
          WebkitBackdropFilter: scrolled ? 'saturate(180%) blur(14px)' : 'none',
          borderBottom: `1px solid ${
            isBrandArea
              ? 'rgba(255,255,255,0.14)'
              : scrolled
              ? COLORS.hairline
              : 'transparent'
          }`,
          boxShadow: scrolled ? '0 6px 24px rgba(14,27,31,0.08)' : 'none',
          transition:
            'background-color .3s ease, box-shadow .3s ease, border-color .3s ease',
          py: scrolled ? '4px' : '10px',
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            width: { xs: '90%', md: '80%' },
            mx: 'auto',
            gap: 2,
            minHeight: { xs: 56, md: 64 },
            transition: 'min-height .3s ease',
          }}
        >
          {isMobile && (
            <>
              {!isRegisterProfessionalPage && isDrawerButtonAvailable && (
                <IconButton onClick={handleClickOpenNavMenu}>
                  <MenuIcon
                    htmlColor={
                      isRegisterProfessionalHome || isProfessionalPage
                        ? COLORS.white
                        : COLORS.ink
                    }
                  />
                </IconButton>
              )}

              <Drawer
                keepMounted
                ref={el => {
                  drawerRef.current = el;
                  shouldMobileNavTogglerVisible();
                }}
                anchor='right'
                open={isDrawerOpen}
                onClose={handleCloseDrawer}
                PaperProps={{
                  sx: {
                    width: '78%',
                    maxWidth: 320,
                    backgroundColor: isProfessionalPage
                      ? COLORS.teal
                      : COLORS.surface,
                    p: 2.5,
                    gap: 1.5,
                    alignItems: 'stretch',
                  },
                }}
              >
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                  mb={1}
                >
                  <img
                    alt='logo'
                    src={isProfessionalPage ? WhiteLogo : GreenLogo}
                    style={{ width: 120, height: 40, objectFit: 'contain' }}
                  />
                  <IconButton onClick={handleCloseDrawer}>
                    <CloseIcon
                      htmlColor={isProfessionalPage ? COLORS.white : COLORS.ink}
                    />
                  </IconButton>
                </Stack>
                <NavItemHome />
                <NavItemLogout />
                <NavItemSignIn
                  onClick={() => {
                    handleCloseDrawer();
                    makeSignInModelVisible();
                  }}
                />
              </Drawer>
            </>
          )}

          <Link
            variant='h6'
            component={RouterLink}
            to='/'
            sx={{
              display: 'inline-flex',
              transition: 'transform .25s ease',
              '&:hover': { transform: 'scale(1.03)' },
            }}
          >
            <img
              alt='logo'
              src={logoSrc}
              style={{
                width: isMobile ? 120 : 172,
                height: isMobile ? 40 : 58,
                objectFit: 'contain',
                transition: 'all .3s ease',
              }}
            />
          </Link>

          {!isMobile && (
            <Stack
              mr={1}
              ml='auto'
              direction='row'
              gap={2}
              alignItems='center'
              sx={{
                // Sliding underline for text nav links.
                '& .MuiButton-text': {
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: 14,
                    right: 14,
                    bottom: 6,
                    height: '2px',
                    borderRadius: 2,
                    backgroundColor: isBrandArea ? '#fff' : COLORS.teal,
                    transform: 'scaleX(0)',
                    transformOrigin: 'left',
                    transition: 'transform .28s cubic-bezier(0.22,1,0.36,1)',
                  },
                  '&:hover::after': { transform: 'scaleX(1)' },
                },
              }}
            >
              <NavItemHome />
              <NavItemLogout />
              <NavItemSignIn onClick={makeSignInModelVisible} />
              {!isLoggedUser && !isRegisterProfessionalPage && (
                <TypoNav variant='body2'>Are you a Professional?</TypoNav>
              )}
              <NavItemJoin />
              <NavItemDashboard />
              <NavItemLoggedUser />
            </Stack>
          )}

          {isMobile && (
            <Stack direction='row' alignItems='center' gap={1} ml='auto'>
              <NavItemJoin />
              <NavItemDashboard />
              <NavItemLoggedUser />
            </Stack>
          )}
        </Toolbar>
      </AppBar>

      <Signup
        open={isJoinModalVisible}
        onClose={makeJoinModelHidden}
        openLoginModal={makeSignInModelVisible}
      />

      <Login
        open={isSignInModalVisible}
        onClose={makeSignInModelHidden}
        onClickSignUpNowText={() => {
          makeSignInModelHidden();
          makeJoinModelVisible();
        }}
      />
    </>
  );
};

Header.propTypes = {
  isSellerOrCategoryPage: PropTypes.bool,
  isRegisterProfessionalPage: PropTypes.bool,
  isProfessionalPage: PropTypes.bool,
  isUserPage: PropTypes.bool,
  isHomePage: PropTypes.bool,
};

export default React.memo(Header);
